<template>
	<div
		v-htmlclass="{'show--custom-dropdown': customDropdownOpen}"
		class="select-input"
		:class="{
			'select-input--disabled': disabled,
			'select-input--focused': focused,
			'select-input--error': error,
			'select-input--empty': !options.length,
			'select-input--no-selection': !value,
			'select-input--custom-dropdown-open': customDropdownOpen,
			'select-input--custom-dropdown-top': customDropdownTop,
		}"
		v-on="customDropdownEnabled ? {
			focus: onFocusCustom,
			focusout: onBlurCustom,
			keydown: onKeydownCustom,
			click: onValueClickCustom,
		} : {}"
		:tabindex="customDropdownEnabled ? 0 : -1"
	>
		<FormInput
			:value="computedValue"
			:label="label"
			:label-option="labelOption"
			:placeholder="label"
			readonly
			:disabled="disabled"
			:error="error"
		/>
		<ul
			ref="dropdown"
			class="select-input__dropdown"
			v-if="customDropdownEnabled"
			@mouseenter="onDropdownMouseEnter"
			@mouseleave="onDropdownMouseLeave"
		>
			<template
				v-for="(option, index) in options"
			>
				<template
					v-if="option.group"
				>
					<li
						class="select-input__dropdown-group"
						:key="option.label"
						v-html="option.label"
					>
					</li>

					<li
						v-for="(opt, key) in option.options"
						ref="dropdownOption"
						:key="opt.value"
						class="select-input__dropdown-option select-input__dropdown-option--grouped"
						:class="{
							'select-input__dropdown-option--disabled': !!opt.disabled,
							'select-input__dropdown-option--selected': opt.value === value,
							'select-input__dropdown-option--hover': allOptions.findIndex(option => option.value == opt.value) === customDropdownHover,
						}"
						:data-value="opt.value"
						:disabled="!!opt.disabled"
						v-html="opt.label || opt.value"
						@click.prevent="onOptionClickCustom($event, opt)"
						@mouseover="onOptionHoverCustom($event, opt, `${index}-${key}`)"
					/>
				</template>
				<li
					v-else
					ref="dropdownOption"
					:key="option.value"
					class="select-input__dropdown-option"
					:class="{
						'select-input__dropdown-option--disabled': !!option.disabled,
						'select-input__dropdown-option--selected': option.value === value,
						'select-input__dropdown-option--hover': index === customDropdownHover,
					}"
					:data-value="option.value"
					:disabled="!!option.disabled"
					v-html="option.label || option.value"
					@click.prevent="onOptionClickCustom($event, option)"
					@mouseover="onOptionHoverCustom($event, option, index)"
				/>
			</template>
		</ul>
		<select
			v-if="!customDropdownEnabled"
			class="select-input__select"
			ref="input"
			:id="id"
			:name="name"
			:required="required"
			:disabled="disabled || readonly"
			:value="value"
			@change="onInput"
			@focus="onFocus"
			@blur="onBlur"
		>
			<option
				value=""
				disabled
				v-html="label"
			/>
			<template
				v-for="option in options"
			>
				<optgroup
					v-if="option.group"
					:label="option.label"
					:key="option.label"
				>
					<option
						v-for="opt in option.options"
						:key="opt.value"
						:value="opt.value"
						:disabled="!!opt.disabled"
						v-html="opt.label || opt.value"
					/>
				</optgroup>
				<option
					v-else
					:key="option.value"
					:value="option.value"
					:disabled="!!option.disabled"
					v-html="option.label || option.value"
				/>
			</template>
		</select>
	</div>
</template>

<script>
import FormInput from './FormInput';
import {isIos, isAndroid, isTouch} from '@/utils/features';
import throttle from '@/utils/throttle';

export default {
	components: {
		FormInput,
	},
	props: {
		id: String,
		name: String,
		value: Number | String,
		required: Boolean,
		disabled: Boolean,
		readonly: Boolean,
		error: Boolean,
		label: String,
		labelOption: String,
		options: Array,
	},
	data() {
		return {
			focused: false,
			customDropdownOpen: false,
			customDropdownTop: false,
			customDropdownHover: null,
			customDropdownMouseover: false,
			ignoreFollowingClick: false,
		}
	},
	computed: {
		computedValue() {
			const option = this.allOptions.filter(option => option.value == this.value)[0];
			return option && !option.reset ? option.label || option.value : '';
		},
		allOptions() {
			return [
				...this.options,
				...this.options
					.filter(option => option.group)
					.reduce((options, option) => {
						return [
							...options,
							...option.options,
						]
					}, [])
			].filter(option => !option.group && option.value !== undefined);
		},
		customDropdownEnabled() {
			// return false
			return !isIos && !isAndroid;
		}
	},
	model: {
		prop: 'value',
		event: 'input'
	},
	methods: {
		onInput(e) {
			this.$emit('input', e.target.value);
		},
		onFocus(e) {
			this.focused = true;
			this.$emit('focus', e);
		},
		onBlur(e) {
			this.focused = false;
			this.$emit('blur', e);
		},
		showCustomDropdown() {
			if (this.disabled) {
				return;
			}

			const height = this.$el.clientHeight;
			const windowHeight = window.innerHeight;

			const offsetTop = this.$el.getBoundingClientRect().top;
			const offsetBottom = offsetTop + height;
      const maxDropdownHeight = windowHeight - offsetTop - height;

			this.$refs.dropdown.removeAttribute('style');

			if (!this.$refs.dropdownOption || !this.$refs.dropdownOption.length) {
				return;
			}

      const dropdownHeight = this.$refs.dropdown.clientHeight;
			const selectedItem = this.$refs.dropdownOption.find(option => option.dataset.value == this.value);

      // if (maxDropdownHeight / windowHeight > 0.5 || maxDropdownHeight >= dropdownHeight) {
      if (maxDropdownHeight >= 120 || maxDropdownHeight >= dropdownHeight) {
				this.customDropdownTop = false;
				this.$refs.dropdown.style.maxHeight = `${maxDropdownHeight}px`;
      }
      else {
				this.customDropdownTop = true;
				this.$refs.dropdown.style.maxHeight = `${offsetTop}px`;
      }
			this.customDropdownOpen = true;
			this.$refs.dropdown.scrollTop = 0;

			if (selectedItem) {
				this.$refs.dropdown.scrollTop = selectedItem.offsetTop;
			}
		},
		hideCustomDropdown() {
			this.customDropdownOpen = false;
			this.customDropdownHover = null;
		},
		onKeydownCustom(e) {
			const char = String.fromCharCode(e.which).toLowerCase();
			let prev;
			let next;
			let matchingItems;
			let matchPosition;
			let selectedIndex = this.$refs.dropdownOption.findIndex(option => option.dataset.value == this.value);

			if (selectedIndex < 0) {
				selectedIndex = null;
			}

			if (e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				this.hideCustomDropdown();
			}
			else if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				e.stopPropagation();
				if (this.customDropdownHover !== null) {
					const selectedOption = this.allOptions[this.customDropdownHover];
					if (!selectedOption.disabled) {
						this.$emit('input', this.allOptions[this.customDropdownHover].value);
						this.hideCustomDropdown();
					}
				}
			}
			else if (e.key !== 'Tab' && !(e.ctrlKey||e.metaKey||e.altKey||e.shiftKey)) {
				e.preventDefault();
				e.stopPropagation();
				this.showCustomDropdown();
				this.ignoreFollowingClick = false;

				if (e.key === 'ArrowUp') {
					if (this.customDropdownHover === null) {
						this.customDropdownHover = selectedIndex !== null ? selectedIndex : 0;
					}
					else {
						this.customDropdownHover = Math.max(this.customDropdownHover - 1, 0);
					}
					this.$refs.dropdown.scrollTop = this.$refs.dropdownOption[this.customDropdownHover].offsetTop;
				}
				else if (e.key === 'ArrowDown') {
					if (this.customDropdownHover === null) {
						this.customDropdownHover = selectedIndex !== null ? selectedIndex : 0;
					}
					else {
						this.customDropdownHover = Math.min(this.customDropdownHover + 1, this.$refs.dropdownOption.length - 1);
					}
					this.$refs.dropdown.scrollTop = this.$refs.dropdownOption[this.customDropdownHover].offsetTop;
				}
				else {
					matchingItems = [];

					matchingItems = this.allOptions.reduce((a, option, index) => {
						if ((''+(option.label || option.value)).toLowerCase().charAt(0) == char && !option.disabled) {
							a.push(index);
						}
						return a;
					}, []);

					matchPosition = matchingItems.reduce((a, index) => {
						if (
								((matchingItems.includes(this.customDropdownHover) && this.customDropdownHover < index) ||
								(!matchingItems.includes(this.customDropdownHover) && this.customDropdownHover !== index)) &&
								a === null
						) {
							return index;
						}
						return a;
					}, null);

					if (matchPosition === null) {
						matchPosition = 0;
					}
					this.customDropdownHover = matchPosition;
					this.$refs.dropdown.scrollTop = this.$refs.dropdownOption[this.customDropdownHover].offsetTop;
				}
			}
		},
		onFocusCustom(e) {
			this.focused = true;
			this.ignoreFollowingClick = true;
			this.showCustomDropdown();
			this.$emit('focus', e);
		},
		onBlurCustom(e) {
			if (!this.customDropdownMouseover) {
				this.focused = false;
				this.hideCustomDropdown();
				this.$emit('blur', e);
			}
		},
		onValueClickCustom(e) {
			if (e.target === this.$el) {
				if (this.customDropdownOpen && !this.ignoreFollowingClick) {
					this.hideCustomDropdown();
					this.$emit('blur', e);
				} else {
					this.showCustomDropdown();
				}
				this.ignoreFollowingClick = false;
			}
		},
		onDropdownMouseEnter() {
			this.customDropdownMouseover = true;
		},
		onDropdownMouseLeave() {
			this.customDropdownMouseover = false;
		},
		onOptionClickCustom(e, option) {
			if (!option.disabled) {
				this.hideCustomDropdown();
				this.$emit('input', option.value);
			}
		},
		onOptionHoverCustom(e, option, index) {
			this.customDropdownHover = this.allOptions.findIndex(o => o.value == option.value);
		},
		onWindowResize() {
			if (this.customDropdownOpen) {
				this.showCustomDropdown();
			}
		}
	},
	mounted() {
		this.boundWindowResize = throttle(this.onWindowResize.bind(this));
		window.addEventListener('scroll', this.boundWindowResize);
		window.addEventListener('resize', this.boundWindowResize);
	},
	destroy() {
		window.removeEventListener('scroll', this.boundWindowResize);
		window.removeEventListener('resize', this.boundWindowResize);
	}
}
</script>

<style>

</style>
